import { useId } from "@reach/auto-id";
import PopperJS from "popper.js";
import React, { Children, cloneElement, FocusEvent, useEffect, useRef, useState } from "react";
import Box, { BoxProps } from "../box/Box";
import CloseButton from "../CloseButton";
import Popper, { PopperArrow, PopperProps } from "../popper/Popper";
import { setRef, wrapEvent } from "../utils";
import usePrevious from "../utils/usePrevious";
import PopoverContext, { usePopoverContext } from "./PopoverContext";

type InternalState = { isOpen?: boolean; onClose?: () => void };

type PopoverChildren =
  | {
      children: React.ReactNode;
    }
  | { children: (props: InternalState) => React.ReactNode };

interface IPopover {
  /**
   * The html `id` attribute of the popover.
   * If not provided, we generate a unique id.
   *
   * This `id` is also used to auto-generate the `aria-labelledby`
   * and `aria-decribedby` attributes that points to the `PopoverHeader` and `PopoverBody`
   */
  id?: string;
  /**
   * If `true`, the popover will be opened in controlled mode.
   */
  isOpen?: boolean;
  /**
   * If `true`, the popover will be initially opened.
   */
  defaultIsOpen?: boolean;
  /**
   * The `ref` of the element that should receive focus when the popover opens.
   */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /**
   * The interaction that triggers the popover.
   *
   * `hover` - means the popover will open when you hover with mouse or
   * focus with keyboard on the popover trigger
   *
   * `click` - means the popover will open on click or
   * press `Enter` to `Space` on keyboard
   */
  trigger?: "hover" | "click";
  /**
   * If `true`, the popover will return focus to the trigger when it closes
   */
  returnFocusOnClose?: boolean;
  /**
   * The gap (in pixels) to apply between the popover and the target.
   * Used by `popper.js`
   */
  gutter?: number;
  /**
   * The placment of the popover
   */
  placement?: PopperJS.Placement;
  /**
   * If `true`, the popover will close when you blur out it by
   * clicking outside or tabbing out
   */
  closeOnBlur?: boolean;
  /**
   * If `true`, the popover will close when you hit the `Esc` key
   */
  closeOnEsc?: boolean;
  /**
   * Callback fired when the popover opens
   */
  onOpen?: () => void;
  /**
   * Callback fired when the popover closes
   */
  onClose?: () => void;
  /**
   * If true the popover is displayed with a Portal.
   * Rendering content inside a Portal allows the popover content
   * to escape the physical bounds of its parent while still being
   * positioned correctly relative to its target
   */
  usePortal?: boolean;
}

interface IPopoverTrigger {
  children: React.ReactElement;
}

const PopoverTrigger: React.FC<IPopoverTrigger> = ({ children }: IPopoverTrigger) => {
  const { referenceRef, popoverId, onToggle, trigger, onOpen, isOpen, onClose, isHoveringRef } = usePopoverContext();

  const child = Children.only(children);
  let eventHandlers = {};

  if (trigger === "click" && child) {
    eventHandlers = {
      onClick: wrapEvent(child.props.onClick, onToggle),
    };
  }

  const openTimeout = useRef(null);

  if (trigger === "hover") {
    eventHandlers = {
      onFocus: wrapEvent(child.props.onFocus, onOpen),
      onKeyDown: wrapEvent(child.props.onKeyDown, (event) => {
        if (event.key === "Escape") {
          setTimeout(onClose, 300);
        }
      }),
      onBlur: wrapEvent(child.props.onBlur, onClose),
      onMouseEnter: wrapEvent(child.props.onMouseEnter, () => {
        if (isHoveringRef) {
          setRef(isHoveringRef, true);
        }

        if (openTimeout) {
          setRef(openTimeout, setTimeout(onOpen, 300));
        }
      }),
      onMouseLeave: wrapEvent(child.props.onMouseLeave, () => {
        setRef(isHoveringRef, false);

        if (openTimeout.current) {
          clearTimeout(openTimeout.current || undefined);
          openTimeout.current = null;
        }
        setTimeout(() => {
          if (isHoveringRef && isHoveringRef.current === false) {
            onClose();
          }
        }, 300);
      }),
    };
  }

  return cloneElement(child, {
    "aria-haspopup": "dialog",
    "aria-expanded": isOpen,
    "aria-controls": popoverId,
    ref: referenceRef,
    ...eventHandlers,
  });
};

interface IPopoverContent {
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  gutter?: number;
  /**
   * If the `PopoverHeading` isn't rendered, use this prop to add
   * an accessible label to the popover.
   */
  "aria-label"?: string;
}

const PopoverContent: React.FC<IPopoverContent & PopperProps> = ({
  onKeyDown,
  onBlur: onBlurProp,
  onMouseLeave,
  onMouseEnter,
  gutter = 4,
  "aria-label": ariaLabel,
  ...props
}: IPopoverContent & PopperProps) => {
  const {
    popoverRef,
    referenceRef,
    placement,
    popoverId,
    isOpen,
    onBlur,
    closeOnEsc,
    onClose,
    isHoveringRef,
    trigger,
    headerId,
    bodyId,
    usePortal,
  } = usePopoverContext();
  const bg = "white";

  let eventHandlers = {};
  let roleProps = {};

  if (trigger === "click") {
    eventHandlers = {
      onBlur: wrapEvent(onBlurProp, onBlur),
    };

    roleProps = {
      role: "dialog",
      "aria-modal": "false",
    };
  }

  if (trigger === "hover") {
    eventHandlers = {
      onMouseEnter: wrapEvent(onMouseEnter, () => {
        setRef(isHoveringRef, true);
      }),
      onMouseLeave: wrapEvent(onMouseLeave, () => {
        setRef(isHoveringRef, false);
        setTimeout(onClose, 300);
      }),
    };

    roleProps = {
      role: "tooltip",
    };
  }

  eventHandlers = {
    ...eventHandlers,
    onKeyDown: wrapEvent(onKeyDown, (event) => {
      if (event.key === "Escape" && closeOnEsc) {
        onClose && onClose();
      }
    }),
  };

  return (
    <Popper
      as="section"
      usePortal={usePortal}
      isOpen={isOpen}
      placement={placement}
      aria-label={ariaLabel}
      anchorEl={referenceRef ? referenceRef.current : null}
      ref={popoverRef}
      bg={bg}
      id={popoverId}
      aria-hidden={!isOpen}
      tabIndex={-1}
      borderWidth="1px"
      width="100%"
      position="relative"
      display="flex"
      flexDirection="column"
      rounded="md"
      shadow="sm"
      maxWidth="xs"
      zIndex={"popover" as any}
      modifiers={{ offset: { enabled: true, offset: `0, ${gutter}` } }}
      _focus={{ outline: 0, shadow: "outline" }}
      aria-labelledby={headerId}
      aria-describedby={bodyId}
      {...roleProps}
      {...eventHandlers}
      {...props}
    />
  );
};

const Popover: React.FC<IPopover & PopoverChildren> = ({
  id,
  isOpen: isOpenProp,
  initialFocusRef,
  defaultIsOpen,
  usePortal = false,
  returnFocusOnClose = true,
  trigger = "click",
  placement = "auto",
  children,
  closeOnBlur = true,
  closeOnEsc = true,
  onOpen: onOpenProp,
  onClose: onCloseProp,
}: IPopover & PopoverChildren) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen || false);
  const { current: isControlled } = useRef(isOpenProp != null);

  const isHoveringRef = useRef<boolean | null>();

  const referenceRef = useRef<HTMLElement>();
  const popoverRef = useRef<HTMLElement | null>();

  const _isOpen = isControlled ? isOpenProp : isOpen;

  const onToggle = () => {
    if (!isControlled) {
      setIsOpen(!_isOpen);
    }

    if (!_isOpen === true) {
      onOpenProp && onOpenProp();
    } else {
      onCloseProp && onCloseProp();
    }
  };

  const onOpen = () => {
    if (!isControlled) {
      setIsOpen(true);
    }

    if (onOpenProp) {
      onOpenProp();
    }
  };

  const onClose = () => {
    if (!isControlled) {
      setIsOpen(false);
    }

    if (onCloseProp) {
      onCloseProp();
    }
  };

  const handleBlur = (event: FocusEvent) => {
    if (
      _isOpen &&
      closeOnBlur &&
      popoverRef.current &&
      referenceRef.current &&
      !popoverRef.current.contains(event.relatedTarget as any) &&
      !referenceRef.current.contains(event.relatedTarget as any)
    ) {
      onClose();
    }
  };

  // A unique fallback id in case the id prop wasn't passed
  const fallbackId = `popover-${useId()}`;
  const popoverId = id || fallbackId;

  const headerId = `${popoverId}-header`;
  const bodyId = `${popoverId}-body`;

  const prevIsOpen = usePrevious(_isOpen);

  useEffect(() => {
    if (_isOpen && trigger === "click") {
      requestAnimationFrame(() => {
        if (initialFocusRef && initialFocusRef.current) {
          initialFocusRef.current.focus();
        } else if (popoverRef.current) {
          popoverRef.current.focus();
        }
      });
    }

    if (!_isOpen && prevIsOpen && trigger === "click" && returnFocusOnClose) {
      if (referenceRef.current) {
        referenceRef.current.focus();
      }
    }
  }, [_isOpen, popoverRef, initialFocusRef, trigger, referenceRef, prevIsOpen, returnFocusOnClose]);

  const context = {
    popoverRef,
    placement,
    referenceRef,
    headerId,
    bodyId,
    popoverId,
    onOpen,
    onClose,
    onToggle,
    trigger,
    isOpen: _isOpen,
    onBlur: handleBlur,
    closeOnEsc,
    initialFocusRef,
    isHoveringRef,
    usePortal,
  };

  return (
    <PopoverContext.Provider value={context}>
      {typeof children === "function" ? children({ isOpen: _isOpen, onClose }) : children}
    </PopoverContext.Provider>
  );
};

const PopoverHeader: React.FC<BoxProps> = (props: BoxProps) => {
  const { headerId } = usePopoverContext();

  return <Box as="header" id={headerId} px={3} py={2} borderBottomWidth="1px" {...props} />;
};

const PopoverFooter = (props: BoxProps) => <Box as="footer" px={3} py={2} borderTopWidth="1px" {...props} />;

const PopoverBody: React.FC<BoxProps> = (props: BoxProps) => {
  const { bodyId } = usePopoverContext();

  return <Box id={bodyId} flex="1" px={3} py={2} {...props} />;
};

const PopoverArrow: React.FC<BoxProps> = (props: BoxProps) => <PopperArrow {...props} />;

type TPopoverCloseButtonProps = Omit<BoxProps, "size"> & {
  onClick?: React.MouseEventHandler<HTMLElement>;
  size?: "lg" | "md" | "sm";
};

const PopoverCloseButton: React.FC<TPopoverCloseButtonProps> = ({ onClick, ...props }: TPopoverCloseButtonProps) => {
  const { onClose } = usePopoverContext();
  return (
    <CloseButton
      size="md"
      onClick={wrapEvent(onClick, onClose)}
      aria-label="Close"
      pos="absolute"
      rounded="md"
      top={1}
      right={2}
      p={2}
      {...props}
    />
  );
};

export {
  PopoverHeader,
  PopoverFooter,
  PopoverBody,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
};
